import React from "react";
import { Link } from "react-router-dom";
import { Shield, BookOpen, ArrowLeft, AlertTriangle, ShieldAlert } from "lucide-react";
import TierBadge from "./TierBadge";

/**
 * Hero for /peptides/:slug — title, category/tier badges, overview,
 * Material-Information regulatory callout, optional PT-141 vs Vyleesi wall,
 * and educational CTAs. Extracted from PeptideDetailPage to keep the parent
 * page under 300 lines and to make the compliance callouts independently
 * testable.
 */
export const PeptideHero = ({ slug, seo, product, tierInfo }) => (
  <section className="pb-16 pt-8 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link
        to="/shop"
        className="inline-flex items-center text-[#b89a6d] hover:text-[#a08560] mb-6 group text-sm"
      >
        <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
        All Products
      </Link>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="px-3 py-1 bg-[#f7e8e4] text-[#b89a6d] text-xs font-medium rounded-full">
          {product?.category || "Research"}
        </span>
        <TierBadge tier={tierInfo.tier} testid={`peptide-${slug}`} />
        {product?.popular && (
          <span className="px-3 py-1 bg-[#fdf2dc] text-[#b89a6d] text-xs font-medium rounded-full">Popular</span>
        )}
        <span className="text-[#a09186] text-sm">{product?.price}</span>
      </div>

      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#3a2a26] mb-6" data-testid="peptide-h1">
        {seo.h1}
      </h1>

      <p className="text-lg text-[#7a6b62] leading-relaxed max-w-4xl">
        {seo.overview}
      </p>

      {/* Material Information — prominent regulatory callout (Tier A compliance) */}
      <div
        className="mt-6 max-w-4xl border-2 border-amber-300 bg-amber-50 rounded-xl p-5"
        data-testid="material-info-callout"
        role="note"
      >
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#5a4a40]">
            <p className="font-bold text-amber-800 mb-1 uppercase tracking-wide text-xs">
              Material Information — Research Use Only · Not FDA-Approved · Not for Human Use
            </p>
            <p className="leading-relaxed">
              This product is a research chemical sold strictly for in-vitro laboratory research. It is not approved by the U.S. Food &amp; Drug Administration for human or veterinary use, is not a drug, and is not a dietary supplement. Nothing on this page is medical advice. See our{" "}
              <Link to="/editorial-standards" className="text-[#b89a6d] hover:text-[#a08560] underline">editorial standards</Link>{" "}
              and{" "}
              <Link to="/compliance" className="text-[#b89a6d] hover:text-[#a08560] underline">compliance framework</Link>{" "}
              for our evidence-tier methodology.
            </p>
          </div>
        </div>
      </div>

      {/* PT-141 — sharp wall vs the FDA-approved bremelanotide (Vyleesi) */}
      {slug === "pt-141" && (
        <div
          className="mt-4 max-w-4xl border-2 border-rose-300 bg-rose-50 rounded-xl p-5"
          data-testid="pt141-bremelanotide-callout"
          role="note"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#5a4a40]">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <p className="font-bold text-rose-700">
                  Research-only PT-141 is NOT Vyleesi&reg; (bremelanotide injection)
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mb-2 text-xs">
                <span className="inline-flex items-center gap-1.5"><TierBadge tier={1} linked={false} /> Vyleesi&reg; (FDA-approved)</span>
                <span className="text-rose-300">vs.</span>
                <span className="inline-flex items-center gap-1.5"><TierBadge tier={3} linked={false} /> PT-141 (this page)</span>
              </div>
              <p className="leading-relaxed">
                Vyleesi&reg; is a regulated, FDA-approved prescription bremelanotide product approved for one specific clinical indication in adult premenopausal women, dispensed by licensed pharmacies under a clinician&apos;s prescription. The research-only material on this page is a different product class entirely: it is sold for laboratory use, has not undergone FDA review, and must not be administered to humans. If you are seeking the regulated product, talk to a licensed clinician.
              </p>
            </div>
          </div>
        </div>
      )}

      {product && (
        <div className="mt-8 flex flex-wrap gap-4">
          {/* Hero CTA is intentionally educational; the affiliate buy CTA is
              rendered below the Research Summary so visitors and crawlers
              absorb regulatory/evidence context first. */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-[#e8c5b8] text-[#b89a6d] font-semibold rounded-lg hover:border-[#c4949c] transition-colors"
            data-testid="peptide-read-research-cta"
          >
            <BookOpen className="w-4 h-4" /> Read the Research
          </Link>
          <Link
            to="/editorial-standards"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-[#e8c5b8] text-[#5a4a40] font-semibold rounded-lg hover:border-[#c4949c] transition-colors"
            data-testid="peptide-editorial-cta"
          >
            <Shield className="w-4 h-4" /> Editorial Standards
          </Link>
        </div>
      )}
    </div>
  </section>
);

export default PeptideHero;
